import { type RefObject, useEffect } from "react";
import { canMatchMedia } from "./useMediaQuery";

// Smallest visual-viewport shrink (px) that counts as a keyboard rather than browser chrome
const MIN_KEYBOARD_HEIGHT = 100;
// Space (px) kept between the input's bottom edge and the keyboard
const KEYBOARD_GAP = 8;
// How far (px) the input is parked off-screen for the focus frame
const PARK_OFFSET = 2000;
// Downward drag (px) from the top of the conversation that closes the keyboard
const PULL_DISMISS_DISTANCE = 40;
// A touch that moves less than this (px) and lifts within this time (ms) is a tap
const TAP_MAX_MOVE = 10;
const TAP_MAX_MS = 500;
// Re-measure times (ms) after a resize or focus, covering the keyboard's slide
const SETTLE_DELAYS_MS = [100, 250, 400, 600, 900];

// Lifts `moveRef` (default `ref`) until focused `ref` clears the phone keyboard; writes the DOM directly to skip re-render lag
export function useKeyboardLift(ref: RefObject<HTMLElement>, moveRef?: RefObject<HTMLElement>, scrollRef?: RefObject<HTMLElement>): void {
  useEffect(() => {
    const viewport = typeof window !== "undefined" ? window.visualViewport : null;
    // Touch only: on desktop a short window (docked DevTools) would read as a keyboard
    const isTouch = canMatchMedia() && window.matchMedia("(pointer: coarse)").matches;
    if (!viewport || !isTouch) return;
    let lift = 0;
    // Page height with the keyboard closed; iOS shrinks innerHeight mid-slide, so it can't be read live
    let fullHeight = window.innerHeight;
    // Re-read on resize (rotation lands here with final sizes, unlike orientationchange), but only with no field focused so the keyboard can't shrink it
    const onResize = () => {
      const active = document.activeElement as HTMLElement | null;
      if (!active?.isContentEditable && !active?.matches?.("input, textarea, select")) fullHeight = window.innerHeight;
    };
    const liftTransform = () => (lift ? `translateY(-${lift}px)` : "");
    const mover = () => moveRef?.current ?? ref.current;
    const blurActive = () => (document.activeElement as HTMLElement | null)?.blur();
    // Top edge of the nearest clipping ancestors, i.e. where the lifted card starts getting cut off
    const clipTop = (el: HTMLElement) => {
      let top = 0;
      for (let node = el.parentElement; node; node = node.parentElement) {
        if (getComputedStyle(node).overflowY !== "visible") top = Math.max(top, node.getBoundingClientRect().top);
      }
      return top;
    };
    // Pads `scrollRef` by the part of it the lift pushes out of view, so its top can still scroll in; skipped when `ref` sits inside it (padding would push the input back down)
    let pad = 0;
    // Lift the padding was last measured for; skips the ancestor walk on keystrokes that don't move the card
    let padFor = 0;
    const applyPad = (next: number) => {
      const scroller = scrollRef?.current;
      const moving = mover();
      if (!scroller || !moving || next === padFor) return;
      padFor = next;
      // Contain overscroll only while lifted so a drag can't squeeze the page; closed, pull-to-refresh works as usual
      scroller.style.overscrollBehaviorY = next ? "contain" : "";
      // `lift` is still the applied value here, so adding it back gives the scroller's resting top
      const hidden = clipTop(moving) - (scroller.getBoundingClientRect().top + lift - next);
      const target = next && !scroller.contains(ref.current) ? Math.max(0, Math.round(hidden)) : 0;
      if (target === pad) return;
      scroller.style.paddingTop = target ? `${target}px` : "";
      scroller.scrollTop += target - pad;
      pad = target;
    };
    const apply = (next: number) => {
      applyPad(next);
      const moving = mover();
      if (next === lift || !moving) return;
      lift = next;
      moving.style.transform = liftTransform();
    };
    // A finger on the page (pull-to-refresh, its bounce) shifts every rect; freeze the lift so the input rides down behind the keyboard instead of squeezing the card
    let touching = false;
    const update = () => {
      if (touching || window.scrollY < 0) return;
      const el = ref.current;
      // Pinch-zoom also shrinks the visual viewport; only a gap of MIN_KEYBOARD_HEIGHT or more at scale 1 is a keyboard
      fullHeight = Math.max(fullHeight, window.innerHeight);
      const keyboardOpen = viewport.scale <= 1.01 && fullHeight - viewport.height >= MIN_KEYBOARD_HEIGHT;
      if (!el || !keyboardOpen || !el.contains(document.activeElement)) {
        apply(0);
        return;
      }
      // Add back the current lift; iOS measures rects from the visible top, so adding offsetTop would double-count
      const bottom = el.getBoundingClientRect().bottom + lift;
      apply(Math.max(0, Math.round(bottom + KEYBOARD_GAP - viewport.height)));
    };
    // The keyboard's final slide frame often fires no event, so re-measure over ~0.9s
    let timers: ReturnType<typeof setTimeout>[] = [];
    const settle = () => {
      update();
      timers.forEach(clearTimeout);
      timers = SETTLE_DELAYS_MS.map((ms) => setTimeout(update, ms));
    };
    // Touch start point, time and scroller-at-top, for tap detection and pull-down dismiss
    let touchStart: { x: number; y: number; at: number; atTop: boolean } | null = null;
    const onTouchStart = (e: TouchEvent) => {
      const touch = e.touches[0];
      const atTop = (scrollRef?.current?.scrollTop ?? 1) <= 0;
      touchStart = e.touches.length === 1 && touch ? { x: touch.clientX, y: touch.clientY, at: Date.now(), atTop } : null;
      touching = true;
    };
    // Last finger up: unfreeze and re-measure once the bounce settles
    const onTouchDone = (e: TouchEvent) => {
      touching = e.touches.length > 0;
      if (!touching) settle();
    };
    // Pulling down from the top of the conversation while lifted closes the keyboard, like a native chat
    const onTouchMove = (e: TouchEvent) => {
      const scroller = scrollRef?.current;
      const touch = e.touches[0];
      // A drag inside the input itself (selecting text on the empty state) isn't a pull-down
      if (!lift || !scroller || !touch || !touchStart?.atTop || !scroller.contains(e.target as Node) || ref.current?.contains(e.target as Node)) return;
      if (touch.clientY - touchStart.y > PULL_DISMISS_DISTANCE) blurActive();
    };
    // Park the box PARK_OFFSET px up for the focus frame so iOS skips its page slide (React Aria's trick)
    const onTouchEnd = (e: TouchEvent) => {
      const el = ref.current;
      const editable = el?.querySelector<HTMLElement>('[contenteditable="true"], textarea');
      if (!el || !editable || !editable.contains(e.target as Node) || editable.contains(document.activeElement)) return;
      const end = e.changedTouches[0];
      const start = touchStart;
      touchStart = null;
      const isTap =
        e.cancelable && e.touches.length === 0 && !!start && !!end &&
        Math.hypot(end.clientX - start.x, end.clientY - start.y) < TAP_MAX_MOVE && Date.now() - start.at < TAP_MAX_MS;
      if (!isTap) return;
      e.preventDefault();
      // Caret from the tap point, read before the box moves
      const caret = document.caretRangeFromPoint?.(end.clientX, end.clientY);
      el.style.transform = `translateY(-${PARK_OFFSET}px)`;
      editable.focus({ preventScroll: true });
      if (caret && editable.contains(caret.startContainer)) {
        const selection = window.getSelection();
        selection?.removeAllRanges();
        selection?.addRange(caret);
      }
      requestAnimationFrame(() => {
        el.style.transform = mover() !== el ? "" : liftTransform();
        settle();
      });
    };
    // Keep focus on button taps, or the blur drops the box before the tap lands
    const onMouseDown = (e: MouseEvent) => {
      const button = (e.target as Element).closest?.("button");
      if (lift && button && ref.current?.contains(button)) e.preventDefault();
    };
    // [data-keyboard-dismiss] buttons (Send) close the keyboard after their click runs
    const onClick = (e: MouseEvent) => {
      const button = (e.target as Element).closest?.("[data-keyboard-dismiss]");
      if (button && ref.current?.contains(button)) setTimeout(blurActive, 0);
    };
    // iOS blurs on its synthetic mousedown; pointerdown is left alone because cancelling it breaks the / menu on Android
    document.addEventListener("mousedown", onMouseDown, { capture: true });
    document.addEventListener("touchstart", onTouchStart, { capture: true, passive: true });
    document.addEventListener("touchmove", onTouchMove, { capture: true, passive: true });
    document.addEventListener("click", onClick, { capture: true });
    document.addEventListener("touchend", onTouchDone, { capture: true, passive: true });
    document.addEventListener("touchcancel", onTouchDone, { capture: true, passive: true });
    document.addEventListener("touchend", onTouchEnd, { capture: true, passive: false });
    window.addEventListener("resize", onResize);
    viewport.addEventListener("resize", settle);
    viewport.addEventListener("scroll", update);
    document.addEventListener("focusin", settle);
    document.addEventListener("focusout", update);
    // Typing re-measures too, so the box keeps its place as the suggestion row comes and goes
    document.addEventListener("input", update);
    return () => {
      timers.forEach(clearTimeout);
      document.removeEventListener("touchend", onTouchEnd, { capture: true });
      document.removeEventListener("touchend", onTouchDone, { capture: true });
      document.removeEventListener("touchcancel", onTouchDone, { capture: true });
      document.removeEventListener("touchstart", onTouchStart, { capture: true });
      document.removeEventListener("touchmove", onTouchMove, { capture: true });
      document.removeEventListener("mousedown", onMouseDown, { capture: true });
      document.removeEventListener("click", onClick, { capture: true });
      window.removeEventListener("resize", onResize);
      viewport.removeEventListener("resize", settle);
      viewport.removeEventListener("scroll", update);
      document.removeEventListener("focusin", settle);
      document.removeEventListener("focusout", update);
      document.removeEventListener("input", update);
      apply(0);
    };
  }, [ref, moveRef, scrollRef]);
}
